<template>
  <PTransparentLoader :loading="loading">
    <CRow v-if="payout">
      <CCol lg="12">
        <div class="d-flex align-items-center mb-3">
          <CButton
              color="link"
              class="p-0 mr-3"
              @click="goBack"
          >
            <CIcon name="cil-arrow-left" size="lg"/>
          </CButton>
          <h1 class="main-header mb-0">
            <translate translate-context="accounting.payout">Payout</translate>
            #{{ payout.id }}
          </h1>
        </div>

        <!-- Payout Info -->
        <CCard class="mb-3">
          <CCardHeader>
            <translate translate-context="accounting.payout">Payout Information</translate>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Status</translate>
                  </small>
                  <CBadge :color="getStatusColor(payout.status)">
                    {{ getStatusLabel(payout.status) }}
                  </CBadge>
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Contract</translate>
                  </small>
                  <strong>{{ payout.contract_name || '-' }}</strong>
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Contract Manager</translate>
                  </small>
                  {{ payout.contract_manager_name || '-' }}
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Created At</translate>
                  </small>
                  {{ formatDate(payout.created_at) }}
                </div>
              </CCol>
            </CRow>

            <CRow>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Period From</translate>
                  </small>
                  {{ formatDate(payout.date_from) }}
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Period To</translate>
                  </small>
                  {{ formatDate(payout.date_to) }}
                </div>
              </CCol>
              <CCol md="3" v-if="payout.paid_at">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Paid At</translate>
                  </small>
                  {{ formatDate(payout.paid_at) }}
                </div>
              </CCol>
              <CCol md="3" v-if="payout.paid_by_email">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Paid By</translate>
                  </small>
                  {{ payout.paid_by_email }}
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Amounts -->
        <CCard class="mb-3">
          <CCardHeader>
            <translate translate-context="accounting.payout">Amounts</translate>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Gross Amount</translate>
                  </small>
                  <strong>{{ formatAmount(payout.paid_gross) }}</strong>
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Refunds</translate>
                  </small>
                  <span class="text-danger">-{{ formatAmount(payout.refunds) }}</span>
                </div>
              </CCol>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Net Amount</translate>
                  </small>
                  <strong class="text-success">{{ formatAmount(payout.paid_net) }}</strong>
                </div>
              </CCol>
            </CRow>

            <CRow>
              <CCol md="3">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Total Orders</translate>
                  </small>
                  {{ payout.num_orders_total }}
                </div>
              </CCol>
              <CCol md="3" v-if="payout.iban">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">IBAN</translate>
                  </small>
                  <code>{{ payout.iban }}</code>
                </div>
              </CCol>
              <CCol md="3" v-if="payout.invoice_number">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Invoice Number</translate>
                  </small>
                  {{ payout.invoice_number }}
                </div>
              </CCol>
              <CCol md="3" v-if="payout.invoice">
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Invoice</translate>
                  </small>
                  <a :href="payout.invoice" target="_blank" class="btn btn-sm btn-outline-primary">
                    <CIcon name="cil-cloud-download" class="mr-1"/>
                    <translate translate-context="accounting.payout">Download PDF</translate>
                  </a>
                </div>
              </CCol>
            </CRow>

            <CRow v-if="payout.notes">
              <CCol>
                <div class="mb-3">
                  <small class="text-muted d-block">
                    <translate translate-context="accounting.payout">Notes</translate>
                  </small>
                  <span class="text-muted">{{ payout.notes }}</span>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Pica Services Breakdown -->
        <CCard class="mb-3" v-if="payout.payout_pica_services && payout.payout_pica_services.length > 0">
          <CCardHeader>
            <translate translate-context="accounting.payout">Events Breakdown</translate>
          </CCardHeader>
          <CCardBody class="px-0">
            <CDataTable
                :items="payout.payout_pica_services"
                :fields="picaServiceFields"
                :hover="false"
                :bordered="true"
                size="sm"
                class="custom-table custom-table--clean mb-0"
            >
              <template #paid_gross="{item}">
                <td class="text-right">
                  {{ formatAmount(item.paid_gross) }}
                </td>
              </template>
              <template #refunds="{item}">
                <td class="text-right text-danger">
                  <span v-if="item.refunds && parseFloat(item.refunds) > 0">
                    -{{ formatAmount(item.refunds) }}
                  </span>
                  <span v-else>---</span>
                </td>
              </template>
              <template #paid_net="{item}">
                <td class="text-right text-success">
                  {{ formatAmount(item.paid_net) }}
                </td>
              </template>
              <template #download="{item}">
                <td class="text-center">
                  <CButton
                    color="primary"
                    size="sm"
                    variant="ghost"
                    @click="handleDownloadPicaService(item)"
                    v-c-tooltip="$pgettext('accounting.payout', 'Download Excel')"
                  >
                    <CIcon name="cil-cloud-download"/>
                  </CButton>
                </td>
              </template>
            </CDataTable>
          </CCardBody>
        </CCard>

      </CCol>
    </CRow>
  </PTransparentLoader>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import { getRoutePrefix } from '../constants'
import payoutStatus from '@/domain/core/constant/payoutStatus'

export default {
  name: 'PayoutDetail',
  components: {
    PTransparentLoader
  },
  data() {
    return {
      loading: true
    }
  },
  async mounted() {
    // Set scope first, then load data
    await this.setScope(this.scope)
    await this.loadPayout()
  },
  computed: {
    ...mapGetters('accounting/payout', ['payout']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      // Note: Payout is not available for pica-service scope
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    listRouteName() {
      return getRoutePrefix(this.scopeType)
    },

    picaServiceFields() {
      return [
        { key: 'name', label: this.$pgettext('accounting.payout', 'Event') },
        { key: 'paid_gross', label: this.$pgettext('accounting.payout', 'Gross') },
        { key: 'refunds', label: this.$pgettext('accounting.payout', 'Refunds') },
        { key: 'paid_net', label: this.$pgettext('accounting.payout', 'Net') },
        { key: 'download', label: '', _style: 'width: 60px;', sorter: false, filter: false }
      ]
    }
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/payout', ['fetchPayout', 'downloadPicaServiceExcel']),

    async handleDownloadPicaService(psItem) {
      try {
        await this.downloadPicaServiceExcel({ payoutId: this.$route.params.id, psId: psItem.pica_service_id })
      } catch (error) {
        // silent fail
      }
    },

    goBack() {
      const params = {}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      }
      this.$router.push({ name: this.listRouteName, params })
    },

    async loadPayout() {
      this.loading = true
      try {
        await this.fetchPayout(this.$route.params.id)
      } finally {
        this.loading = false
      }
    },

    getStatusColor(status) {
      return payoutStatus.color(status)
    },

    getStatusLabel(status) {
      return payoutStatus.label(status)
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },

    formatAmount(amount) {
      const num = parseFloat(amount) || 0
      return num.toFixed(2)
    }
  }
}
</script>
